import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from 'framer-motion';
import LogoBlanc from '../../LogoBlanc';
import LoginShowcase from './LoginShowcase';
import { getTone, grey, lightVeil, toneAlpha, withAlpha } from '../../../theme/tones';
import { gradients, FONT_DISPLAY } from '../../../theme/tokens';

/**
 * Panneau de marque de l'écran de connexion.
 *
 * Même matière que le rail de navigation — le marine du logotype éclairé
 * par ses deux couleurs (halo bleu en haut, lueur orange en bas) — pour
 * que la page de connexion et l'application ne fassent qu'un seul cadre.
 *
 * Le panneau réagit à la souris : une lumière suit le curseur et la scène
 * 3D s'incline légèrement. Tout cela est débrayé quand l'utilisateur a
 * demandé moins d'animations.
 */

const orange = getTone('orange').light;
const marine = getTone('brand');

const WORDS = [
  'Sessions',
  'Apprenants',
  'Conventions',
  'Émargement',
  'Questionnaires',
  'Relances',
  'Financeurs',
  'Intervenants',
  'Statistiques'
];

/** Une passe du bandeau défilant ; rendue deux fois pour boucler sans raccord. */
const TickerRow = () => (
  <>
    {WORDS.map((word) => (
      <Box
        key={word}
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 2.5,
          pr: 2.5,
          fontSize: '0.66rem',
          fontWeight: 700,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: lightVeil(0.4),
          whiteSpace: 'nowrap'
        }}
      >
        {word}
        <Box
          component="span"
          sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: withAlpha(orange, 0.85) }}
        />
      </Box>
    ))}
  </>
);

const LoginBrandPanel = ({ sx = {} }) => {
  const reduce = Boolean(useReducedMotion());

  // Position normalisée de la souris (-1 → 1) pour la parallaxe, et
  // position brute pour la lumière.
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const mx = useMotionValue(-999);
  const my = useMotionValue(-999);

  const spring = { stiffness: 46, damping: 16, mass: 0.9 };
  // Au repos (souris au centre ou absente) la scène garde une légère
  // inclinaison : elle est posée en perspective, pas de face.
  const rotateX = useSpring(useTransform(py, [-1, 1], [11, -3]), spring);
  const rotateY = useSpring(useTransform(px, [-1, 1], [-19, 3]), spring);

  const veil = lightVeil(0.09);
  const spotlight = useTransform(
    [mx, my],
    ([x, y]) => `radial-gradient(620px circle at ${x}px ${y}px, ${veil} 0%, transparent 60%)`
  );

  const handleMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    px.set((x / rect.width) * 2 - 1);
    py.set((y / rect.height) * 2 - 1);
    mx.set(x);
    my.set(y);
  };

  const handleLeave = () => {
    px.set(0);
    py.set(0);
    mx.set(-999);
    my.set(-999);
  };

  return (
    <Box
      onMouseMove={reduce ? undefined : handleMove}
      onMouseLeave={reduce ? undefined : handleLeave}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        color: grey(0),
        background: [
          `radial-gradient(640px 520px at 0% 0%, ${toneAlpha('blue', 0.38)} 0%, ${toneAlpha('blue', 0)} 62%)`,
          `radial-gradient(520px 440px at 100% 100%, ${withAlpha(orange, 0.2)} 0%, ${withAlpha(orange, 0)} 60%)`,
          `linear-gradient(170deg, ${marine.base} 0%, ${marine.deep} 55%, ${marine.deepest} 100%)`
        ].join(', '),
        // Même filet que le rail : une arête claire, pas le gris du thème.
        borderRight: { md: `1px solid ${lightVeil(0.08)}` },
        ...sx
      }}
    >
      {/* Trame fine : donne une échelle au fond sur grand écran. */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: `linear-gradient(${lightVeil(0.05)} 1px, transparent 1px), linear-gradient(90deg, ${lightVeil(0.05)} 1px, transparent 1px)`,
          backgroundSize: '44px 44px',
          maskImage: `radial-gradient(110% 90% at 50% 45%, ${grey(950)} 25%, transparent 78%)`,
          WebkitMaskImage: `radial-gradient(110% 90% at 50% 45%, ${grey(950)} 25%, transparent 78%)`
        }}
      />

      {/* Lumière qui suit le curseur. */}
      <Box
        component={motion.div}
        aria-hidden="true"
        style={{ background: spotlight }}
        sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      />

      {/* ------------------------------------------------------ Marque -- */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          px: { xs: 3, md: 5, lg: 7 },
          pt: { xs: 3, md: 4.5 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box
          component={RouterLink}
          to="/"
          aria-label="Syndays"
          sx={{ display: 'inline-flex', lineHeight: 0 }}
        >
          <LogoBlanc style={{ height: 26, width: 'auto', display: 'block' }} />
        </Box>
      </Box>

      {/* ------------------------------------------------------- Scène -- */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <LoginShowcase
          rotateX={rotateX}
          rotateY={rotateY}
        />
      </Box>

      {/* ------------------------------------------------------ Manifeste -- */}
      <Box
        component={motion.div}
        initial={reduce ? false : { opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
        sx={{
          position: 'relative',
          zIndex: 1,
          px: { xs: 3, md: 5, lg: 7 },
          pt: { xs: 3, md: 0 },
          pb: { xs: 3.5, md: 4.5 }
        }}
      >
        <Typography
          component="p"
          sx={{
            fontFamily: FONT_DISPLAY,
            fontSize: '0.66rem',
            fontWeight: 700,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: withAlpha(orange, 0.9),
            mb: 1.5
          }}
        >
          Plateforme de gestion · Formation continue
        </Typography>

        <Typography
          component="h1"
          sx={{
            fontFamily: FONT_DISPLAY,
            fontWeight: 800,
            fontSize: { xs: '1.5rem', md: 'clamp(1.6rem, 2.2vw, 2.35rem)' },
            letterSpacing: '-0.03em',
            lineHeight: 1.12,
            maxWidth: 560,
            m: 0,
            color: grey(0)
          }}
        >
          <Box
            component="span"
            sx={{
              background: gradients.aurora,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent'
            }}
          >
            Un seul cadre
          </Box>
          {' '}
          pour vos sessions, vos apprenants et vos financeurs.
        </Typography>

        {/* Bandeau défilant : le vocabulaire de l'outil, à mi-voix. */}
        <Box
          aria-hidden="true"
          sx={{
            display: { xs: 'none', md: 'block' },
            mt: 3.5,
            overflow: 'hidden',
            maskImage: `linear-gradient(90deg, transparent 0%, ${grey(950)} 12%, ${grey(950)} 88%, transparent 100%)`,
            WebkitMaskImage: `linear-gradient(90deg, transparent 0%, ${grey(950)} 12%, ${grey(950)} 88%, transparent 100%)`
          }}
        >
          <Box
            component={motion.div}
            animate={reduce ? undefined : { x: ['0%', '-50%'] }}
            transition={{ duration: 42, repeat: Infinity, ease: 'linear' }}
            sx={{ display: 'flex', width: 'max-content' }}
          >
            <TickerRow />
            <TickerRow />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

LoginBrandPanel.propTypes = {
  sx: PropTypes.object
};

export default LoginBrandPanel;
